import Seo from "@/components/Seo";
import { PageHero, SectionHead, CTABand } from "@/components/Section";
import { Reveal } from "@/components/Reveal";

const FOCUS = [
  { title: "Current Technology", text: "The Carbon Free Generator programme: a 500W prototype with a hybrid generation and storage architecture." },
  { title: "Prototype Development", text: "Iterative hardware development — each prototype generation tested, measured and refined." },
  { title: "Engineering", text: "Electrical, mechanical and control-systems engineering conducted to international standards of practice." },
  { title: "Testing & Validation", text: "Performance, endurance and safety validation precede any public technical claim." },
  { title: "Future Energy Systems", text: "Research into storage-first architectures, renewable integration and scalable community power." },
  { title: "Research Partnerships", text: "Open to universities, laboratories and research institutions working on clean-energy systems." },
];

const ROADMAP = [
  { phase: "Now", title: "CFG-500 Prototype", text: "Prototype development, testing and validation of the 500W carbon-free unit." },
  { phase: "Next", title: "5kVA Hybrid System", text: "Scaling the architecture toward a 5kVA-class hybrid carbon-free generator." },
  { phase: "Then", title: "Pilot Programmes", text: "Controlled deployments with institutional and development partners." },
  { phase: "Future", title: "Product Family", text: "Storage systems, community power solutions and regional manufacturing readiness." },
];

export default function Innovation() {
  return (
    <>
      <Seo
        title="Innovation & R&D — BNM Sustainable Energy Limited"
        description="Inside BNM's research and development: prototype engineering, testing and validation, future energy systems and research partnerships in Sierra Leone."
      />
      <PageHero
        testid="innovation-hero"
        eyebrow="Innovation & R&D"
        lines={["Research that ends", "in hardware."]}
        lead="BNM's R&D exists to ship working technology. Every research thread terminates in a prototype, a validation report or a product decision."
      />

      <section data-testid="innovation-focus" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead eyebrow="R&D Focus" title="Where BNM engineering is concentrated." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {FOCUS.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.05}>
                <article className="h-full rounded-lg border border-slate-200 bg-white p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                  <p className="font-mono text-xs text-emerald-600">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="innovation-roadmap" className="relative overflow-hidden bg-obsidian noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            dark
            eyebrow="Technology Roadmap"
            title="The path from CFG-500 to a product family."
            lead="Timelines are confirmed with partners as milestones complete — we publish progress, not projections."
          />
          <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {ROADMAP.map((r, i) => (
              <Reveal as="li" key={r.phase} delay={i * 0.08}>
                <div className={`h-full rounded-lg border p-7 ${i === 0 ? "border-volt/50 bg-pine" : "border-white/10 bg-pine/60"}`}>
                  <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${i === 0 ? "text-volt" : "text-slate-500"}`}>
                    {r.phase}
                  </p>
                  <h3 className="mt-3 font-display text-lg font-bold tracking-tight text-white">{r.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <CTABand
        title="Research with BNM."
        text="Universities, laboratories and technology companies: partner with us on clean-energy generation, storage and hybrid systems research."
        primary={{ label: "Propose a Research Partnership", to: "/contact", testid: "innovation-contact-cta" }}
        secondary={{ label: "See Our Technology", to: "/technology", testid: "innovation-tech-cta" }}
        testid="innovation-cta"
      />
    </>
  );
}
